import { ReactNode, useEffect, useRef, useState } from 'react'

import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons'

import Box from 'components/atomic/atoms/box'
import { IconStyled } from 'components/atomic/atoms/icon'
import Text from 'components/atomic/atoms/text'

type Tab = {
  title: string
  content: ReactNode
  current?: boolean
}

type Props = {
  tabs: Tab[]
}

const SharedTabs = (props: Props) => {
  const { tabs } = props

  const [internalTabs, setInternalTabs] = useState(tabs)

  const ref = useRef<HTMLDivElement>(null)
  const [minHeight, setMinHeight] = useState(0)

  useEffect(() => {
    const content = ref.current
    if (content) {
      const currentHeight = content.getBoundingClientRect().height

      if (minHeight < currentHeight) {
        setMinHeight(currentHeight)
      }
    }
  }, [minHeight, tabs])

  const onTabChange = index => {
    setInternalTabs(t => t.map((tab, i) => ({ ...tab, current: i === index })))
  }

  return (
    <>
      <Box
        css={{
          borderRadius: '$lg',
          border: '1px solid $bg2',
          bgc: '$bg1',
          display: 'block',
          '@md': { display: 'none' }
        }}
      >
        {internalTabs.map(({ title, content, current }, index) => (
          <Box
            key={index}
            css={{
              '&:not(:last-child)': {
                borderBottom: '1px solid $bg2'
              }
            }}
          >
            <Box
              as="button"
              flex
              items="center"
              justify="between"
              onClick={() => onTabChange(index)}
              css={{
                p: '$4',
                width: '$full',
                textAlign: 'left',
                flex: 1,
                transition: 'border-bottom .15s ease-in',
                borderBottom: current
                  ? '1px solid $accent'
                  : '1px solid $transparent'
              }}
            >
              <Text
                size="lg"
                color={current ? 'text1' : 'text3'}
                css={{ fontFamily: '$mono' }}
              >
                {title}
              </Text>
              <Box>
                <IconStyled
                  as={current ? ChevronUpIcon : ChevronDownIcon}
                  color={current ? 'text1' : 'text3'}
                />
              </Box>
            </Box>
            {current && <Box css={{ p: '$4' }}>{content}</Box>}
          </Box>
        ))}
      </Box>
      <Box
        css={{
          borderRadius: '$lg',
          border: '1px solid $bg2',
          bgc: '$bg1',
          display: 'none',
          '@md': { display: 'block' }
        }}
      >
        <Box
          flex
          css={{
            borderTopRadius: '$lg',
            boxShadow: 'inset 0 -1px $colors$bg2',
            bgc: 'rgba(206, 206, 206, 0.3)',
            '.dark &': {
              bgc: 'rgba(0, 0, 0, 0.35)'
            }
          }}
        >
          {internalTabs.map(({ title, current }, index) => (
            <Box
              key={index}
              as="button"
              onClick={() => onTabChange(index)}
              css={{
                py: '$4',
                textAlign: 'center',
                flex: 1,
                transition: 'border-bottom .15s ease-in',
                borderBottom: current
                  ? '1px solid $accent'
                  : '1px solid $transparent'
              }}
            >
              <Text
                size="lg"
                color={current ? 'text1' : 'text3'}
                css={{ fontFamily: '$mono' }}
              >
                {title}
              </Text>
            </Box>
          ))}
        </Box>
        <Box ref={ref} css={{ p: '$4', minHeight }}>
          {internalTabs.find(({ current }) => !!current)?.content}
        </Box>
      </Box>
    </>
  )
}

export default SharedTabs
